import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {MediaCardType} from '@wandb/weave/common/types/media';
import {firstGrouped} from '@wandb/weave/common/util/data';
import {clsx} from 'clsx';
import React, {useMemo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';

import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {ClassLabelMap, getMediaMetadata} from '../../util/media';
import {StaticAxis} from '../Axis';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {Axis, MediaBrowserPanelConfig} from './common';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  calcGroupCount,
  getPanelContentSizing,
  Layout,
  MediaBrowserPanelProps,
  SizingSettings,
} from './Component';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import PanelTileGroup from './PanelTileGroup';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {getPaginatedTiles, TilePagination} from './tiles';

export type PanelContentProps = {
  disableRunLinks?: boolean;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
  config: Omit<MediaBrowserPanelConfig, 'chartTitle'>;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  // NOTE:  These values are derived from config. We could move these out of
  // the argument and into helper function .e.g: getActiveAxes
  labels: Axis[];
  sizingSettings: SizingSettings;
  mediaType: MediaCardType;
  layout: Layout;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  allMasks: ClassLabelMap;
  currentStep: number;
  tilePagination: TilePagination | null;
  customRunNames?: CustomRunNames;
  isMaxStepLoading: boolean;
};

const PanelContentComp = (props: PanelContentProps) => {
  const {
    mediaType,
    layout,
    mediaBrowserSize,
    config,
    disableRunLinks,
    currentStep,
    tilePagination,
    sizingSettings,
    allMasks,
    labels,
    customRunNames,
    isMaxStepLoading,
  } = props;
  const {columnCount} = sizingSettings;

  const firstTile = firstGrouped(layout.tiles);
  const mediaSliderKey = useMediaSliderKey(config.mediaSliderKey);
  const firstMediaMetadata =
    firstTile != null && !firstTile.isEmpty && firstTile.run.history != null
      ? getMediaMetadata(firstTile.run.history, firstTile.mediaKey, currentStep)
      : null;

  const {cardWidth, cardHeight, shrunk} = getPanelContentSizing({
    config,
    mediaType,
    mediaBrowserSize,
    sizingSettings,
    firstMediaMetadata,
  });
  const showActualSize = config.actualSize || config.fitToDimension;

  // Sampled x-Axis data
  const xAxisData = layout.gridAxisData?.x;
  // const yAxisData =
  //   layout.axisData instanceof Array ? null : layout.axisData.y;
  // const xAxisZoom = config.zoom?.xAxis;
  const allXAxisValues = layout.allXAxisValues;
  const showXAxisSettings =
    config.gridSettings?.xAxis === 'step' &&
    xAxisData != null &&
    layout.xAxisTicks != null &&
    allXAxisValues != null &&
    (config.columnCount ?? 1) > 1;

  const paginatedTiles = useMemo(
    () => getPaginatedTiles(layout.tiles, tilePagination),
    [layout.tiles, tilePagination]
  );

  const groupCount = calcGroupCount(config);
  const stablePanelId = useStablePanelId();

  if (firstMediaMetadata == null) {
    return (
      <PanelError
        panelID={stablePanelId}
        panelViewType="Media Browser"
        mediaType={mediaType ?? undefined}
        message={
          <div>
            Selected runs do not have media for given keys:
            <br />
            {config.mediaKeys?.join(', ')}
          </div>
        }
        logErrorMessage={`Selected runs do not have media for given keys: ${config.mediaKeys?.join(', ')}`}
      />
    );
  }

  const showXAxis =
    showXAxisSettings &&
    xAxisData != null &&
    layout.xAxisTicks != null &&
    allXAxisValues != null &&
    !isMaxStepLoading;

  return (
    <div className="flex w-full flex-col">
      {showXAxis && (
        <StaticAxis
          className={`panel-media__x-axis-top panel-media__x-axis-top--revealed`}
          ticks={xAxisData}
          direction="top"
          itemWidth={cardWidth}
          width={mediaBrowserSize.width}
        />
      )}
      <Card.Group
        style={{
          gridTemplateColumns: `repeat(${columnCount.current},${(
            100 / columnCount.current
          ).toFixed(2)}%`,
          display: `${paginatedTiles.length > 0 ? 'grid' : 'flex'}`,
          justifyItems: 'center',
          margin: 0,
        }}
        className={clsx('media-cards items-start justify-center', {
          'media-cards--pixelated': config.pixelated,
          'media-cards--table': mediaType === 'table',
          'media-cards--shrunk': shrunk,
          'media-cards--actual': showActualSize,
          'media-cards--full': !showActualSize,
        })}>
        {paginatedTiles.map((tileOrGroup, tileIndex) => (
          <PanelTileGroup
            key={tileIndex}
            tileOrGroup={tileOrGroup}
            tileIndex={tileIndex}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            config={config}
            allMasks={allMasks}
            mediaType={mediaType}
            mediaSliderKey={mediaSliderKey}
            labels={labels}
            panelRef={props.panelRef}
            disableRunLinks={disableRunLinks}
            customRunNames={customRunNames}
            columnCount={columnCount.current}
            groupCount={groupCount}
          />
        ))}
      </Card.Group>
    </div>
  );
};
export const PanelContent = React.memo(PanelContentComp);
